import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faArrowLeft, faSpinner, faLock } from '@fortawesome/free-solid-svg-icons';
import { AuroraBackground } from '../ui/aurora-background';
import { businessAuthAPI } from '../../services/api';
import SEO from '../SEO';
import { seoConfig } from '../../utils/seoData';

const ResetCodeVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'tr';
  const email = location.state?.email || '';
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // If no email in state, redirect to forgot password
    if (!email) {
      navigate(`/${currentLang}/forgot-password`);
    }
  }, [email, navigate, currentLang]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    // Resend cooldown timer
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newCode.every(digit => digit !== '') && newCode.length === 6) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setVerificationCode(digits);
      setError(null);
      inputRefs.current[5]?.focus();
      // Auto-verify after paste
      setTimeout(() => {
        handleVerify(pastedData);
      }, 100);
    }
  };

  const handleVerify = async (code?: string) => {
    const codeToVerify = code || verificationCode.join('');
    
    if (codeToVerify.length !== 6) {
      setError('Lütfen 6 haneli kodu girin');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await businessAuthAPI.verifyResetCode({
        email,
        code: codeToVerify
      });

      // API returns a token in the response
      const resetToken = response.data.token || response.data.resetToken;
      
      if (resetToken) {
        // Navigate to reset password page with token
        navigate(`/${currentLang}/reset-password`, { state: { email, token: resetToken } });
      } else {
        setError('Token alınamadı. Lütfen tekrar deneyin.');
        setVerificationCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Doğrulama kodu hatalı. Lütfen tekrar deneyin.');
      setVerificationCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    setError(null);

    try {
      await businessAuthAPI.forgotPassword({ email });
      setResendCooldown(60); // 60 second cooldown
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kod gönderilirken bir hata oluştu.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <SEO {...seoConfig.login} />
      <AuroraBackground className="w-full h-full overflow-scroll">
        <div className="w-full min-h-screen flex items-center justify-center relative overflow-hidden py-10">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900"></div>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>

          {/* Floating Elements */}
          <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/10 rounded-full blur-xl animate-float"></div>
          <div className="absolute top-40 right-20 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-pink-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }}></div>

          <div className="relative z-10 w-full max-w-lg mx-auto px-6">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl">
              {/* Back Button */}
              <button
                onClick={() => navigate(`/${currentLang}/forgot-password`)}
                className="flex items-center space-x-2 text-white/80 hover:text-white transition-colors mb-6"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Geri</span>
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-purple-700/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FontAwesomeIcon icon={faLock} className="text-purple-400 text-3xl" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Kod Doğrulama
                </h1>
                <p className="text-white/70 mb-2">
                  <span className="font-semibold text-white">{email}</span> adresine gönderilen 6 haneli şifre sıfırlama kodunu girin.
                </p>
              </div>

              {/* Verification Code Input */}
              <div className="mb-6">
                <div className="flex justify-center gap-3 mb-4">
                  {verificationCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { inputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      className="w-14 h-14 md:w-16 md:h-16 text-center text-2xl font-bold bg-white/5 border-2 border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all"
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>

              {error && (
                <div className="bg-red-500/20 text-red-400 p-3 rounded-lg text-center mb-4">
                  {error}
                </div>
              )}

              {/* Verify Button */}
              <button
                onClick={() => handleVerify()}
                disabled={isLoading || verificationCode.some(digit => !digit)}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-4 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    <span>Doğrulanıyor...</span>
                  </div>
                ) : (
                  'Doğrula'
                )}
              </button>

              {/* Resend Code */}
              <div className="text-center">
                <p className="text-white/60 text-sm mb-2">
                  Kod gelmedi mi?
                </p>
                <button
                  onClick={handleResendCode}
                  disabled={isResending || resendCooldown > 0}
                  className="text-purple-400 hover:text-purple-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {isResending ? (
                    <span className="flex items-center justify-center space-x-2">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      <span>Gönderiliyor...</span>
                    </span>
                  ) : resendCooldown > 0 ? (
                    `Yeniden gönder (${resendCooldown}s)`
                  ) : (
                    'Kodu Yeniden Gönder'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </AuroraBackground>
    </>
  );
};

export default ResetCodeVerification;

